import moment from 'moment';
import Utils from './Utils';

const EMAIL_LAYOUT_TYPE_DISTRIBUTOR_REPORT = 'distributor-report';

const PREVIEW_FILM_TITLES = {
    fr: 'Le Fabuleux Destin d\'Amélie Poulain',
    en: 'Amélie',
    de: 'Die fabelhafte Welt der Amélie',
    it: 'Il favoloso mondo di Amélie',
};

/** Kept in sync with the engine report labels (stats/pdf_report.js). */
const PREVIEW_LABELS = {
    fr: { entries: 'Entrées', scanned: 'Scannées', gross: 'Brut', exempt: 'Net', flat_fee: 'Forfait', vat_included: 'TVA comprise', total: 'Total', idiv: 'IDIV' },
    en: { entries: 'Entries', scanned: 'Scanned', gross: 'Gross', exempt: 'Net', flat_fee: 'Flat fee', vat_included: 'VAT included', total: 'Total', idiv: 'IDIV' },
    de: { entries: 'Eintritte', scanned: 'Gescannt', gross: 'Brutto', exempt: 'Netto', flat_fee: 'Pauschale', vat_included: 'MwSt. inkl.', total: 'Gesamt', idiv: 'IDIV' },
    it: { entries: 'Entrate', scanned: 'Scansionati', gross: 'Lordo', exempt: 'Netto', flat_fee: 'Forfait', vat_included: 'IVA inclusa', total: 'Totale', idiv: 'IDIV' },
};

/** Languages available for distributor report layouts and contact-language resolution. */
export const DISTRIBUTOR_REPORT_LANGS = Object.keys(PREVIEW_LABELS);

const WEEK_LINE = {
    fr: (week, start, end) => `${week}. Semaine du ${start} au ${end}`,
    de: (week, start, end) => `${week}. Woche vom ${start} bis ${end}`,
    en: (week, start, end) => `${week}. Week from ${start} to ${end}`,
    it: (week, start, end) => `${week}. Settimana dal ${start} al ${end}`,
};

function lang_key(lang) {
    const key = String(lang || 'fr').substring(0, 2).toLowerCase();
    return PREVIEW_LABELS[key] ? key : 'fr';
}

function resolve_flat_fee_amount(stat) {
    const from_total = Number(stat?.days_total?.distributor_flat_fee);
    if (Number.isFinite(from_total) && from_total > 0)
        return from_total;
    const films = Object.values(stat?.days || {});
    return Math.max(0, ...films.map(film => Number(film?.distributor_flat_fee) || 0));
}

function append_flat_fee_summary_totals(totals, amount, labels, money, ticket_gross) {
    if (!(amount > 0))
        return totals;
    totals.push({
        label: `${labels.flat_fee} (${labels.vat_included})`,
        value: money(amount),
    });
    totals.push({ label: labels.total, value: money((Number(ticket_gross) || 0) + amount) });
    return totals;
}

function round_up_to_5_cents(amount) {
    const n = Number(amount) || 0;
    return Math.ceil(Math.round(n * 100) / 5) * 5 / 100;
}

function pricing_idiv_rate(pricing, setting) {
    if (pricing && ('idiv' in pricing) && Number.isFinite(Number(pricing.idiv)))
        return Number(pricing.idiv);
    const local = Number(setting?.customer?.tax?.local);
    return Number.isFinite(local) ? local : 0;
}

function report_shows_idiv(stat, setting) {
    const settings_local = Number(setting?.customer?.tax?.local);
    if (Number.isFinite(settings_local) && settings_local > 0)
        return true;
    let found = false;
    Object.values(stat?.prices || {}).forEach(type_group => {
        Object.values(type_group || {}).forEach(by_price => {
            Object.values(by_price || {}).forEach(pricing => {
                if (pricing && Number(pricing.idiv) > 0)
                    found = true;
            });
        });
    });
    return found;
}

function compute_report_idiv_total(stat, setting, currency) {
    if (!report_shows_idiv(stat, setting))
        return null;

    let idiv_footer_total = 0;
    let representative_rate = 0;
    const settings_local = Number(setting?.customer?.tax?.local);
    if (Number.isFinite(settings_local) && settings_local > 0)
        representative_rate = settings_local;

    Object.entries(stat?.prices || {}).forEach(([type_id, type_group]) => {
        Object.values(type_group || {}).forEach(by_price => {
            Object.values(by_price || {}).forEach(pricing => {
                const sold = Number(pricing?.stats?.nbTotal) || 0;
                const cancelled = Number(pricing?.stats?.nbCancelled) || 0;
                if (!(pricing?.stats) || (sold <= 0 && cancelled <= 0))
                    return;
                const unit_price = (pricing.price || {})[currency];
                const unit_value = (pricing.value || {})[currency];
                const per_screening = pricing.value_per_screening;
                const cancel_value = (per_screening && typeof per_screening === 'object')
                    ? (Number(per_screening[currency]) || 0)
                    : (Number(unit_value) || 0);
                const per_seat = sold > 0
                    ? (Number(pricing.stats.total) || 0) / sold
                    : cancel_value;
                let taxed_value = Number.isFinite(per_seat) ? per_seat : (Number(unit_value) || 0);
                if (type_id === 'one-time-pass') {
                    const price_num = Number(unit_price) || 0;
                    if (price_num > taxed_value)
                        taxed_value = price_num;
                }
                const rate = pricing_idiv_rate(pricing, setting);
                if (rate > 0)
                    representative_rate = rate;
                if (sold > 0) {
                    const idiv_unit = round_up_to_5_cents(taxed_value * rate / 100);
                    idiv_footer_total += idiv_unit * sold;
                }
            });
        });
    });

    if (!(representative_rate > 0) && !(idiv_footer_total > 0))
        return null;

    return { rate: representative_rate, total: idiv_footer_total };
}

function append_idiv_summary_totals(totals, idiv_info, labels, money) {
    if (!idiv_info || (!(idiv_info.rate > 0) && !(idiv_info.total > 0)))
        return totals;
    const rate_s = Number.isFinite(Number(idiv_info.rate)) ? String(idiv_info.rate) : '';
    const label = rate_s ? `${labels.idiv} (${rate_s}%)` : labels.idiv;
    totals.push({ label, value: money(idiv_info.total || 0) });
    return totals;
}

function esc_html(value) {
    return String(value ?? '').replace(/[&<>]/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;',
    }[c]));
}

function build_period_line(lang, report) {
    if (report.period)
        return report.period;
    if (report.start && report.end)
        return `${report.start} - ${report.end}`;
    return '';
}

export function is_kronos_distributor_id(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ''));
}

/**
 * Plain-text line for [distributor_report.summary] in e-mail subjects.
 *
 * @param {Object} report
 * @returns {string}
 */
export function build_distributor_report_summary_plain(report) {
    const parts = [];
    if (report?.film_title)
        parts.push(report.film_title);
    const period = build_period_line(null, report || {});
    if (period)
        parts.push(period);
    return parts.join(' — ');
}

/**
 * HTML block for [distributor_report.summary] (aligned with engine DistributorReportShortcode).
 *
 * @param {Object} report
 * @returns {string}
 */
export function build_distributor_report_summary_html(report) {
    const location = [report.hall, report.cinema, report.city]
        .filter(v => v !== undefined && v !== null && String(v).trim() !== '')
        .map(esc_html)
        .join(' - ');

    const rows = (report.totals || []).map(row =>
        `<tr>` +
        `<td style="padding: 4px 12px 4px 0;">${esc_html(row.label)}</td>` +
        `<td style="padding: 4px 0; text-align: right;">${esc_html(row.value)}</td>` +
        `</tr>`
    ).join('');

    let html = '<div class="tkt-distributor-report">';
    if (report.film_title)
        html += `<p><strong>${esc_html(report.film_title)}</strong></p>`;
    if (report.version || location)
        html += `<p>${[esc_html(report.version), location].filter(v => v).join(' ')}</p>`;
    const period = build_period_line(null, report);
    if (period)
        html += `<p>${esc_html(period)}</p>`;
    if (rows)
        html += `<table style="border-collapse: collapse;">${rows}</table>`;
    html += '</div>';
    return html;
}

/**
 * Sample distributor report context for email layout preview.
 *
 * @param {string} lang
 * @param {string} [currency]
 * @returns {Object}
 */
export function build_preview_distributor_report(lang, currency = 'CHF') {
    const key = lang_key(lang);
    const labels = PREVIEW_LABELS[key];
    const start = moment().subtract(6, 'days').locale(key).format('L');
    const end = moment().locale(key).format('L');
    const week = moment().isoWeek();

    const report = {
        film_title: PREVIEW_FILM_TITLES[key],
        week,
        start,
        end,
        period: (WEEK_LINE[key] || WEEK_LINE.fr)(week, start, end),
        version: 'VF',
        cinema: 'Cinéma Exemple',
        hall: 'Salle 1',
        city: 'Lausanne',
        totals: [
            { label: labels.entries, value: '128' },
            { label: labels.gross, value: `0.00 ${currency}` },
            { label: `${labels.exempt} 3`, value: `0.00 ${currency}` },
            { label: `${labels.flat_fee} (${labels.vat_included})`, value: `1'200.00 ${currency}` },
            { label: labels.total, value: `1'200.00 ${currency}` },
        ],
    };

    return {
        ...report,
        summary: build_distributor_report_summary_html(report),
        summary_plain: build_distributor_report_summary_plain(report),
    };
}

function resolve_film_title_from_id(id, lang, default_lang) {
    const title = id?.title;
    if (typeof title === 'string')
        return title;
    return Utils.resolveEventTranslatedTitle(title, lang, default_lang)
        || title?.original
        || id?.film
        || '';
}

/**
 * Build distributor_report context from live movie stats (send modal preview).
 *
 * @param {{ stat: Object, filters: Object, lang: string, setting: Object, scanned_only: boolean }} params
 * @returns {Object|null}
 */
export function build_distributor_report_context_from_stat({ stat, filters, lang, setting, scanned_only }) {
    if (!stat?.days)
        return null;

    const key = lang_key(lang);
    const default_lang = String(setting?.l10n?.locales?.default || 'fr').substring(0, 2).toLowerCase();
    const currency = setting?.l10n?.currency || 'CHF';
    const labels = PREVIEW_LABELS[key] || PREVIEW_LABELS.fr;

    const first_key = Object.keys(stat.days)[0];
    const id = stat.days[first_key]?._id || {};
    const pstats = stat.prices_total?.stats || {};
    const days_total = stat.days_total || {};
    const start_s = moment(filters?.start_date).locale(key).format('L');
    const end_s = moment(filters?.end_date).locale(key).format('L');
    const hall = Array.isArray(id.hall) ? id.hall[0] : id.hall;
    const money = v => `${Number(v || 0).toFixed(2)} ${currency}`;

    const report = {
        film_title: resolve_film_title_from_id(id, key, default_lang),
        week: id.week,
        start: start_s,
        end: end_s,
        period: (WEEK_LINE[key] || WEEK_LINE.fr)(id.week, start_s, end_s),
        version: id.version,
        cinema: id.cinema,
        hall,
        city: id.city,
        totals: [
            {
                label: scanned_only ? labels.scanned : labels.entries,
                value: String(days_total.nbTotal || 0),
            },
            { label: labels.gross, value: money(pstats.total) },
            { label: `${labels.exempt} 3`, value: money(pstats.total_net3) },
        ],
    };

    const fee = resolve_flat_fee_amount(stat);
    append_flat_fee_summary_totals(report.totals, fee, labels, money, pstats.total);
    append_idiv_summary_totals(
        report.totals,
        compute_report_idiv_total(stat, setting, currency),
        labels,
        money
    );

    return {
        ...report,
        summary: build_distributor_report_summary_html(report),
        summary_plain: build_distributor_report_summary_plain(report),
    };
}

/**
 * Resolve report e-mail language (aligned with engine resolve_report_lang).
 *
 * @param {string|undefined} ui_lang
 * @param {Object} distributor
 * @param {Object} setting
 * @returns {string}
 */
export function resolve_distributor_report_lang(ui_lang, distributor, setting) {
    const locales = setting?.l10n?.locales || {};
    const default_locale = String(locales.default || 'fr').substring(0, 2).toLowerCase();

    const contact_languages = (distributor?.contact_languages || []).map(l =>
        String(l).substring(0, 2).toLowerCase()
    );
    const supported = contact_languages.find(l => DISTRIBUTOR_REPORT_LANGS.includes(l));
    if (supported)
        return supported;

    if (ui_lang)
        return String(ui_lang).substring(0, 2).toLowerCase();

    return default_locale;
}

const DISTRIBUTOR_ENRICH_FIELDS = [
    'id', 'name', 'report_mail', 'contact_mail', 'contact_languages',
    'address', 'zip', 'city', 'country', 'phone', 'title', 'firstname', 'lastname', 'maccsbox_id',
];

/**
 * Merge stat snapshot with Kronos distributor record (Kore wins when filled).
 *
 * @param {Object|null} stat_distributor
 * @param {Object|null} kronos_distributor
 * @returns {Object|null}
 */
export function merge_distributor_sources(stat_distributor, kronos_distributor) {
    if (!stat_distributor && !kronos_distributor)
        return null;

    const merged = { ...(stat_distributor || {}) };

    DISTRIBUTOR_ENRICH_FIELDS.forEach(field => {
        const stat_val = stat_distributor?.[field];
        const kronos_val = kronos_distributor?.[field];
        const stat_filled = stat_val !== undefined && stat_val !== null && String(stat_val).trim() !== '';
        const kronos_filled = kronos_val !== undefined && kronos_val !== null && String(kronos_val).trim() !== '';

        if (kronos_filled)
            merged[field] = kronos_val;
        else if (stat_filled)
            merged[field] = stat_val;
        else if (kronos_val !== undefined)
            merged[field] = kronos_val;
        else if (stat_val !== undefined)
            merged[field] = stat_val;
    });

    merged.id = kronos_distributor?.id || kronos_distributor?._id || stat_distributor?.id;
    if (merged.id && !is_kronos_distributor_id(merged.id))
        delete merged.id;

    return merged;
}

/**
 * Detect whether an e-mail layout is the distributor report template.
 *
 * @param {Object} layout
 * @param {Object} setting
 * @returns {boolean}
 */
export function is_distributor_report_email_layout(layout, setting) {
    if (!layout || layout.layout_type !== 'email')
        return false;

    if (layout.type)
        return layout.type === EMAIL_LAYOUT_TYPE_DISTRIBUTOR_REPORT;

    if (setting?.email_layouts?.distributor_report &&
        String(setting.email_layouts.distributor_report) === String(layout._id))
        return true;

    return Utils.langs().some(lang => {
        const text = (layout.widgets || [])
            .filter(w => w.type === 'text')
            .map(w => w.options?.text?.[lang] || '')
            .join(' ');
        return text.includes('[distributor_report.');
    });
}
